import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom'; 
import './App.css';
import { MapPin, MessageCircle, ArrowDown, Heart, Sparkles, Gift, Shirt, Ticket, Volume2, VolumeX, Calendar } from 'lucide-react';

import { listaInvitados } from './data/invitados';

import pedida_viendo_anillo from './image/viendonos_morelia.jpeg';
import entre_secciones from './image/viendo_anillo.jpeg';
import galeria1 from './image/navidad_juntos.jpeg';
import galeria2 from './image/primera_cita_novios.jpeg';
import galeria3 from './image/fuera_del_cine.jpeg';
import galeria4 from './image/foto_en_yuriria.jpeg';

// Importa tu canción
import musicaFondo from './music/Harold y Elena – Por siempre (Videoclip Oficial) - Harold y Elena.mp3'; 

const weddingData = {
  groom: "Jaziel",
  bride: "Celina",
  date: "2026-01-31T13:30:00",
  locationName: "Salón de Eventos La Muralla",
  locationAddress: "Guanajuato, Gto.",
  locationUrl: "https://maps.app.goo.gl/t18vTHi5qs6RyhsbA", 
  phones: { groom: "524621315049", bride: "524439446527" },
  images: {
    hero: pedida_viendo_anillo, 
    divider: entre_secciones,
    grid1: galeria1,
    grid2: galeria2,
    grid3: galeria3,
    grid4: galeria4
  },
  dressCode: "Elegante"
};

const Countdown = ({ targetDate }) => {
  const calculateTimeLeft = () => {
    const difference = +new Date(targetDate) - +new Date();
    if (difference <= 0) return { dias: 0, horas: 0, min: 0, seg: 0 };
    return {
      dias: Math.floor(difference / (1000 * 60 * 60 * 24)),
      horas: Math.floor((difference / (1000 * 60 * 60)) % 24),
      min: Math.floor((difference / 1000 / 60) % 60),
      seg: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setTimeout(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearTimeout(timer);
  });

  const timerComponents = Object.keys(timeLeft).map((interval) => {
    if (!timeLeft[interval] && timeLeft[interval] !== 0) return null;
    return (
      <div className="time-item" key={interval}>
        <span className="time-val">{timeLeft[interval]}</span>
        <span className="time-label">{interval}</span>
      </div>
    );
  });

  return (
    <div className="countdown-wrapper">
      {timerComponents.length ? timerComponents : <span>¡Es hoy!</span>}
    </div>
  );
};

function Invitation() {
  const { id } = useParams(); 
  const [invitado, setInvitado] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- LÓGICA DE MÚSICA ---
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio(musicaFondo);
    audioRef.current.loop = true; 
    audioRef.current.volume = 0.5; 

    const playPromise = audioRef.current.play();

    if (playPromise !== undefined) {
      playPromise.then(() => {
        setIsPlaying(true);
      }).catch(error => {
        setIsPlaying(false);
        console.log("Autoplay bloqueado (normal).");
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleMusic = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // --- LÓGICA DE SCROLL AUTOMÁTICO DE PÁGINA COMPLETA ---
  const [autoScroll, setAutoScroll] = useState(true); 

  useEffect(() => {
    if (loading || !autoScroll) return; 

    const scrollInterval = setInterval(() => {
      window.scrollBy(0, 1);
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
        setAutoScroll(false);
      }
    }, 40); 

    return () => clearInterval(scrollInterval);
  }, [loading, autoScroll]);

  useEffect(() => {
    const stopScrolling = () => {
      if (autoScroll) {
        setAutoScroll(false); 
      }
    };
    window.addEventListener("wheel", stopScrolling);      
    window.addEventListener("touchmove", stopScrolling);  
    window.addEventListener("keydown", stopScrolling);    
    window.addEventListener("mousedown", stopScrolling);  

    return () => {
      window.removeEventListener("wheel", stopScrolling);
      window.removeEventListener("touchmove", stopScrolling);
      window.removeEventListener("keydown", stopScrolling);
      window.removeEventListener("mousedown", stopScrolling);
    };
  }, [autoScroll]);


  const addToCalendar = () => {
    const title = encodeURIComponent("Boda Celina & Jaziel 💍");
    const details = encodeURIComponent("¡Nos casamos! Gracias por acompañarnos.");
    const location = encodeURIComponent(`${weddingData.locationName}, ${weddingData.locationAddress}`);
    const start = "20260131T133000";
    const end = "20260131T230000";
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&ctz=America/Mexico_City`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (id && listaInvitados[id]) {
      setInvitado(listaInvitados[id]);
    }
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <Heart className="loading-heart" size={60} fill="#86895d" stroke="#86895d" />
          <h2 className="loading-names">{weddingData.bride} & {weddingData.groom}</h2>
          <div className="loading-sparkles">
            <Sparkles className="loading-sparkle" size={24} />
            <Sparkles className="loading-sparkle" size={20} />
            <Sparkles className="loading-sparkle" size={24} />
          </div>
        </div>
      </div>
    );
  }

  const welcomeMessage = invitado?.mensaje || 
    "Será un día lleno de amor, gratitud y momentos inolvidables, y nos encantará contar con tu presencia para celebrarlo juntos.";

  return (
    <div className="layout">

      {/* --- NUEVO: FONDO DIFUMINADO PARA MÓVIL --- */}
      <div 
        className="mobile-bg" 
        style={{ backgroundImage: `url(${weddingData.images.hero})` }} 
      />
      
      {/* SECCIÓN 1: IMAGEN LATERAL (Normal en Móvil, Sticky en Desktop) */}
      <div className="side-image-container">
        <img src={weddingData.images.hero} alt="Pareja" className="side-image" />
        <div className="overlay-mobile">
            <h1 className="mobile-title">{weddingData.bride} & {weddingData.groom}</h1>
        </div>
      </div>

      {/* SECCIÓN 2: CONTENIDO */}
      <div className="content-container">
        {/* Header */}
        <section className="section header-section">
          <div className="sparkles-container">
            <Sparkles className="sparkle sparkle-1" size={20} />
            <Sparkles className="sparkle sparkle-2" size={16} />
          </div>
          <p className="overline" style={{fontSize: '0.9rem', letterSpacing: '3px'}}>
            CON LA BENDICIÓN DE DIOS Y DE NUESTRAS FAMILIAS
          </p>
          <h1 className="desktop-title">
            {weddingData.bride} <span className="amp" style={{color:'#C5A065'}}>&</span> {weddingData.groom}
          </h1>
          <Heart className="heart-icon" size={24} fill="#d4af37" stroke="#d4af37" />
          <p className="verse" style={{marginTop: '20px', fontStyle: 'italic'}}>
            Tenemos el honor de invitarte a <strong> nuestra boda.</strong>
          </p>
          <p className="scroll-down-text" style={{fontWeight: 'bold', color: '#C5A065'}}>
            "Las muchas aguas no podrán apagar el amor, <br />
            Ni lo ahogarán los ríos." <br />
            - Cantares 8:7 -
          </p>
          <ArrowDown className="scroll-icon" size={24} />
        </section>

        {/* Fecha */}
        <section className="section date-section">
          <div className="celebration-banner">
            <Heart className="floating-heart heart-left" size={20} fill="#d4af37" stroke="#d4af37" />
            <div className="date-big">
              <span>31</span>
              <span className="month-year">ENE 2026</span>
            </div>
            <Heart className="floating-heart heart-right" size={20} fill="#d4af37" stroke="#d4af37" />
          </div>
          <p className="time-text">1:30 PM</p>
          <Countdown targetDate={weddingData.date} />
          <br/>
          <button 
            onClick={addToCalendar} 
            className="btn-outline" 
            style={{
              marginTop: '30px', 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
            }}
          >
            <Calendar size={18} /> AGREGAR AL CALENDARIO
          </button>
        </section>

        {/* BOLETO PERSONALIZADO */}
        <section className="section welcome-msg-section">
            {invitado && (
              <div className="ticket-container" style={{
                border: '1px solid #C5A065',
                padding: '20px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.5)', // Más transparente aquí para que se vea el fondo
                margin: '0 auto 30px auto',
                maxWidth: '400px'
              }}>
                <p style={{fontSize: '0.7rem', fontWeight: 'bold', color: '#C5A065', letterSpacing: '3px', textTransform: 'uppercase', marginBottom:'10px'}}>
                  INVITACIÓN ESPECIAL PARA
                </p>
                <h2 style={{fontFamily: 'Cormorant Garamond', fontSize: '2rem', margin: '0 0 15px 0', color: '#5A6E5A', lineHeight: '1.1'}}>
                  {invitado.nombre}
                </h2>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#333'}}>
                  <Ticket size={20} color="#C5A065" />
                  <span style={{fontSize: '1.1rem', fontWeight: 'bold'}}>
                    {invitado.pases} {invitado.pases === 1 ? 'Lugar' : 'Lugares'} Reservados
                  </span>
                </div>
                {invitado.ninos > 0 && (
                   <p style={{fontSize: '0.85rem', color: '#333', marginTop: '5px'}}>
                     ({invitado.adultos} Adultos / {invitado.ninos} {invitado.ninos === 1 ? 'Niño' : 'Niños'})
                   </p>
                )}
              </div>
            )}
            <p className="welcome-text">"{welcomeMessage}"</p>
        </section>

        {/* Ubicación y Dress code */}
        <section className="section location-section">
          <MapPin size={28} className="gold-icon" />
          <h3>CEREMONIA & RECEPCIÓN</h3>
          <p className="venue-name">{weddingData.locationName}</p>
          <p className="venue-address">{weddingData.locationAddress}</p>
          <a href={weddingData.locationUrl} target="_blank" rel="noreferrer" className="btn-outline">
            Ver Mapa
          </a>
          <div className="dress-code-container">
             <div className="divider-small"></div>
             <Shirt size={28} className="gold-icon" style={{marginBottom: '5px'}}/>
             <p className="dress-code-label">CÓDIGO DE VESTIMENTA</p>
             <p className="dress-code-value" style={{whiteSpace: 'pre-line'}}>{weddingData.dressCode}</p>
          </div>
        </section>

        {/* Regalo */}
        <section className="section gift-section">
          <Gift size={28} className="gold-icon" />
          <h3>DETALLE</h3>
          <p className="gift-text" style={{fontWeight: 'bold',}}>Tu presencia es lo más importante para nosotros.</p>
          <p className="gift-text-small">
            Pero si deseas hacernos un detalle, te agradeceremos que sea en un sobre.<br/>
            Lo que venga de tu corazón será recibido con mucho cariño.
          </p>
        </section>

        {/* RSVP */}
        <section className="section rsvp-section">
          <Sparkles className="sparkle-top" size={24} />
          <h3>CONFIRMACIÓN</h3>
          <div style={{maxWidth: '500px', margin: '0 auto 30px auto', lineHeight: '1.6'}}>
            <p style={{fontSize: '1rem', color: '#5A6E5A', marginBottom: '15px'}}>
              Estamos preparando cada detalle con mucho amor. <br />
              <strong>Tu lugar ha sido reservado especialmente para ti.</strong>
            </p>
            <p style={{fontSize: '0.9rem', color: '#555'}}>
              Por favor, ayúdanos confirmando tu asistencia antes del <strong>15 de Enero</strong> para asegurar tu espacio en nuestra celebración.
            </p>
          </div>
          <div className="rsvp-buttons">
            {(!invitado || invitado.host === 'jaziel') && (
              <a 
                href={`https://wa.me/${weddingData.phones.groom}?text=${encodeURIComponent(
                  `Hola Jaziel 👋, soy ${invitado ? invitado.nombre : ''}.\n\n` +
                  `Muchas gracias por la invitación. ✨\n` +
                  `✅ Confirmo con gusto mi asistencia y los ${invitado ? invitado.pases : ''} lugares que han reservado para mí.\n\n` +
                  `¡Ahí nos vemos para celebrar!`
                )}`} 
                className="btn-black" target="_blank" rel="noreferrer"
              >
                <MessageCircle size={16} /> Confirmar con Jaziel
              </a>
            )}
            {(!invitado || invitado.host === 'celina') && (
              <a 
                href={`https://wa.me/${weddingData.phones.bride}?text=${encodeURIComponent(
                  `Hola Celina 👋, soy ${invitado ? invitado.nombre : ''}.\n\n` +
                  `Muchas gracias por la invitación. ✨\n` +
                  `✅ Confirmo con gusto mi asistencia y los ${invitado ? invitado.pases : ''} lugares que han reservado para mí.\n\n` +
                  `¡Ahí nos vemos para celebrar!`
                )}`} 
                className="btn-black" target="_blank" rel="noreferrer"
              >
                <MessageCircle size={16} /> Confirmar con Celina
              </a>
            )}
          </div>
          <div className="exclusive-note">LA INVITACIÓN ES EXCLUSIVA</div>
        </section>

        <footer className="footer-simple">
            <Heart className="footer-heart" size={16} fill="#d4af37" stroke="#d4af37" />
            <small>Con amor, Celina y Jaziel.</small>
            <Heart className="footer-heart" size={16} fill="#d4af37" stroke="#d4af37" />
        </footer>
      </div>

      {/* BOTÓN MÚSICA */}
      <button 
        onClick={toggleMusic}
        style={{
          position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000,
          backgroundColor: '#F9FAF7', border: '1px solid #C5A065',
          borderRadius: '50%', width: '50px', height: '50px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'transform 0.2s'
        }}
        className="music-btn"
      >
        {isPlaying ? (
          <div style={{position: 'relative', display: 'flex'}}>
             <Volume2 size={24} color="#5A6E5A" />
             <span className="ripple"></span>
          </div>
        ) : <VolumeX size={24} color="#999" />}
      </button>

    </div>
  );
}

export default Invitation;